// frontend/src/internal/InternalLogin.js
// 自社スタッフ専用ログインページ
// Supabase Auth でログイン後、 internal_staff テーブルに登録されているかチェック。
// 未登録ならアクセス拒否。

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useInternalStaff } from '../contexts/InternalStaffContext';
import TurnstileWidget from '../components/TurnstileWidget';

const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';

const InternalLogin = () => {
  const navigate = useNavigate();
  const { refreshInternalStaff, logAudit } = useInternalStaff();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  // 🔥 Supabase プロジェクトで CAPTCHA 保護が有効なため、 captcha_token が必須
  const [captchaToken, setCaptchaToken] = useState('');
  // 🔥 Turnstile トークンは 1 回しか使えないため、 ログイン失敗時に再マウントして新トークン取得
  const [captchaResetKey, setCaptchaResetKey] = useState(0);

  const resetCaptcha = () => {
    setCaptchaToken('');
    setCaptchaResetKey((v) => v + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!captchaToken) {
      setError('「私はロボットではありません」のチェックを完了してください');
      return;
    }
    setSubmitting(true);
    try {
      const { error: authErr } = await supabase.auth.signInWithPassword({
        email,
        password,
        options: { captchaToken },
      });
      if (authErr) {
        // Supabase Auth のエラーをわかりやすく
        console.error('Supabase auth error:', authErr);
        const msg = authErr.message || '';
        if (msg.includes('Invalid login credentials')) {
          throw new Error('メールアドレスまたはパスワードが正しくありません。\n'
            + '・Supabase 管理画面 (Authentication > Users) でユーザーが作成されているか確認してください\n'
            + '・「Auto Confirm User」にチェックを入れずに作成した場合、未確認状態でログインできません');
        }
        if (msg.includes('Email not confirmed')) {
          throw new Error('メール未確認の状態です。Supabase 管理画面で Users → 該当ユーザーの「...」→ 「Confirm user」を押してください');
        }
        throw new Error(`Supabase Auth エラー: ${msg}`);
      }
      const internal = await refreshInternalStaff();
      if (!internal) {
        // internal_staff に登録されてない → 即サインアウト
        await supabase.auth.signOut();
        throw new Error('Supabase Auth ログインには成功しましたが、internal_staff テーブルにレコードがありません。\n'
          + 'SQL Editor で以下を実行してください:\n'
          + `INSERT INTO public.internal_staff (user_id, email, name, role) VALUES ('<UUID>', '${email}', '<名前>', 'admin');`);
      }
      await logAudit('login', { detail: { email: internal.email } });
      navigate('/internal/select-org');
    } catch (e) {
      console.error('Internal login error:', e);
      setError(e.message || 'ログインに失敗しました');
      // 🔥 Turnstile トークンは 1 回限り。 失敗したら必ず再取得させる。
      //   timeout-or-duplicate エラーの原因はここ。
      resetCaptcha();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a, #1e3a8a)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
    }}>
      <div style={{
        background: 'white', borderRadius: '16px', padding: '40px',
        maxWidth: '420px', width: '100%',
        boxShadow: '0 20px 50px rgba(0,0,0,0.3)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{
            display: 'inline-block', padding: '6px 14px',
            background: '#fef3c7', color: '#92400e',
            borderRadius: '20px', fontSize: '11px', fontWeight: 700,
            marginBottom: '12px',
          }}>
            🔧 自社スタッフ専用
          </div>
          <h1 style={{ fontSize: '24px', margin: 0, color: '#0f172a' }}>
            ScrubEdge Internal
          </h1>
          <p style={{ color: '#64748b', fontSize: '13px', marginTop: '6px', marginBottom: 0 }}>
            開発者・サポートスタッフ向けログイン
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', marginBottom: '16px' }}>
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>メールアドレス</span>
            <input
              type="email" value={email}
              onChange={(e) => setEmail(e.target.value)}
              required autoComplete="email"
              style={{
                width: '100%', padding: '10px 12px', marginTop: '4px',
                borderRadius: '8px', border: '1px solid #d1d5db',
                fontSize: '14px',
              }}
            />
          </label>
          <label style={{ display: 'block', marginBottom: '20px' }}>
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 600 }}>パスワード</span>
            <input
              type="password" value={password}
              onChange={(e) => setPassword(e.target.value)}
              required autoComplete="current-password"
              style={{
                width: '100%', padding: '10px 12px', marginTop: '4px',
                borderRadius: '8px', border: '1px solid #d1d5db',
                fontSize: '14px',
              }}
            />
          </label>

          {/* 🔥 Turnstile (人間確認 - Supabase 側で CAPTCHA 必須) */}
          <div style={{
            display: 'flex', justifyContent: 'center',
            marginBottom: '16px', minHeight: '65px',
          }}>
            <TurnstileWidget
              key={captchaResetKey}
              siteKey={TURNSTILE_SITE_KEY}
              onVerify={(token) => setCaptchaToken(token)}
              onExpire={() => setCaptchaToken('')}
              onError={() => setCaptchaToken('')}
            />
          </div>

          {error && (
            <div style={{
              background: '#fee2e2', color: '#7f1d1d',
              padding: '10px 12px', borderRadius: '8px',
              fontSize: '12px', marginBottom: '16px',
              whiteSpace: 'pre-line', lineHeight: 1.5,
            }}>{error}</div>
          )}

          <button
            type="submit" disabled={submitting || !captchaToken}
            style={{
              width: '100%', padding: '11px',
              background: (submitting || !captchaToken) ? '#94a3b8'
                : 'linear-gradient(135deg, #1d4ed8, #1e40af)',
              color: 'white', border: 'none', borderRadius: '8px',
              fontWeight: 700, fontSize: '14px',
              cursor: (submitting || !captchaToken) ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'ログイン中...' : 'ログイン'}
          </button>
        </form>

        <div style={{
          marginTop: '24px', paddingTop: '16px',
          borderTop: '1px solid #e5e7eb',
          fontSize: '11px', color: '#94a3b8', textAlign: 'center',
        }}>
          通常の利用者は <a href="/login" style={{ color: '#3b82f6' }}>こちら</a> からログイン
        </div>
      </div>
    </div>
  );
};

export default InternalLogin;
